import { getWorkDate } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { ISmartODataTableAPI } from "@components/smart/smartTable/SmartODataTableBase";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    GovernmentInstitutionEntity,
    IGovernmentInstitutionEntity,
    IPrAttendanceEntity,
    IPrEldpEmployeeEntity,
    IPrEldpEntity,
    IPrEmployeeEntity,
    IPrPayrollSettingEntity,
    OdataActionName,
    PrAttendanceEntity,
    PrEldpEmployeeEntity,
    PrEldpEntity,
    PrEmployeeEntity,
    PrEmployeePermanentAddressEntity,
    PrEmploymentEntity,
    PrPayrollSettingEntity,
    PrSocialSecurityDetailEntity
} from "@odata/GeneratedEntityTypes";
import { GovernmentInstitutionTypeCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { getEmployeeSalaryCurrencyPath } from "@pages/payroll/employee/Employee.utils";
import { desc } from "@utils/general";
import { logger } from "@utils/log";
import memoizeOneWithParamsCheck from "@utils/memoizeOne";

import { ITableStorageDefaultCustomData, TableStorage } from "../../../model/TableStorage";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";


export interface IEldpCustomTableData extends ITableStorageDefaultCustomData {
    selectedYear?: number;
    allYears?: number[];
}

export interface IPrEldpCustomFormData extends IFormStorageDefaultCustomData {
    Year: number;
    governmentInstitutions?: IGovernmentInstitutionEntity[];
    payrollSetting?: IPrPayrollSettingEntity;
}

export type TEldpFormStorage = FormStorage<IPrEldpEntity, IPrEldpCustomFormData>;


export const EldpEmployeePaths = [
    PrEldpEmployeeEntity.TitleBeforeName,
    PrEldpEmployeeEntity.FirstName,
    PrEldpEmployeeEntity.LastName,
    PrEldpEmployeeEntity.BirthLastName,
    PrEldpEmployeeEntity.TitleAfterName,
    PrEldpEmployeeEntity.PersonalIdentificationNumber,
    PrEldpEmployeeEntity.DateOfBirth,
    PrEldpEmployeeEntity.PlaceOfBirth
] as const;

export function getEldpCurrencyPath(): string {
    return getEmployeeSalaryCurrencyPath(createPath(PrEldpEntity.Employee, PrEldpEmployeeEntity.Employee));
}

export function getDateInEldpYear(startEnd: "startOf" | "endOf"): TGetValueFn<Date> {
    return (args: IGetValueArgs) => {
        const year = args.storage.data.entity.Year as number;
        if (!year) {
            return undefined;
        }
        return getUtcDayjs().set("year", year)[startEnd]("year").toDate();
    };
}

export const fetchEldpPayrollSettings = memoizeOneWithParamsCheck(async (storage: TEldpFormStorage): Promise<IPrPayrollSettingEntity> => {
    const { oData } = storage;
    const query = oData.getEntitySetWrapper(EntitySetName.PrPayrollSettings).query()
            .expand(PrPayrollSettingEntity.CzechSocialSecurityAdministration, (q) => {
                q.select(
                        PrSocialSecurityDetailEntity.AccountNumber,
                        PrSocialSecurityDetailEntity.SymbolVariable
                );
            });

    const res = await query.fetchData<IPrPayrollSettingEntity[]>();
    if (res.value?.length !== 1) {
        logger.error("There should be only one PrPayrollSettings entity");
    }
    const payrollSetting = res.value?.[0];
    if (!payrollSetting) {
        logger.error("PrPayrollSettings entity not found");
    }
    storage.setCustomData({ payrollSetting });
    return payrollSetting;
});

export const fetchSocialSecurityAdministrations = memoizeOneWithParamsCheck(async (storage: TEldpFormStorage): Promise<IGovernmentInstitutionEntity[]> => {
    const { oData } = storage;
    const query = oData.getEntitySetWrapper(EntitySetName.GovernmentInstitutions).query()
            .filter(`${GovernmentInstitutionEntity.GovernmentInstitutionTypeCode} eq '${GovernmentInstitutionTypeCode.CzechSocialSecurityAdministration}'`)
            .select(GovernmentInstitutionEntity.Code, GovernmentInstitutionEntity.ShortName, GovernmentInstitutionEntity.OpenDataCode)
            .orderBy(GovernmentInstitutionEntity.ShortName, false);

    const res = await query.fetchData<IGovernmentInstitutionEntity[]>();
    if (res._metadata.count === 0) {
        logger.error("No social security administration found");
    }
    const governmentInstitutions = res.value ?? [];
    storage.setCustomData({ governmentInstitutions });
    return governmentInstitutions;
});

export const fetchEmployeeInfo = async (storage: TEldpFormStorage, Id: number): Promise<IPrEldpEmployeeEntity> => {
    const { oData } = storage;
    const query = oData.getEntitySetWrapper(EntitySetName.PrEmployees).query(Id)
            .select(...EldpEmployeePaths, PrEmployeeEntity.Id)
            .expand(PrEmployeeEntity.PermanentAddress, (q) => {
                q.select(PrEmployeePermanentAddressEntity.Street, PrEmployeePermanentAddressEntity.City, PrEmployeePermanentAddressEntity.PostalCode)
                        .expand(PrEmployeePermanentAddressEntity.Country);
            });

    const res = await query.fetchData<IPrEmployeeEntity>();
    if (!res) {
        logger.error(`Employee with ID ${Id} not found`);
        return undefined;
    }
    const { PermanentAddress, ...employee } = res.value;
    return {
        ...employee,
        PermanentAddress,
        PostOffice: PermanentAddress.City?.slice(0,5) ?? ""
    };
};

export async function fetchELDPYears(storage: TableStorage<ISmartODataTableAPI, IEldpCustomTableData>): Promise<void> {
    // fetch available ELDP years
    const query = storage.oData.getEntitySetWrapper(EntitySetName.PrEldps).query()
            .groupBy(PrEldpEntity.Year)
            .orderBy(PrEldpEntity.Year, false);

    const res = await query.fetchData<IPrEldpEntity[]>();
    const allYears: number[] = res.value?.map(item => item.Year) ?? [];

    const previousYear = getUtcDayjs().get("year") - 1;
    if (!allYears.includes(previousYear)) {
        allYears.push(previousYear);
    }
    allYears.sort(desc);

    const selectedYear = allYears[0];
    storage.setCustomData({
        allYears,
        selectedYear
    });
}

export function getEldpYearFilter(year: number): string {
    return `${PrEldpEntity.Year} eq ${year}`;
}

export async function generateELDPs(oData: OData, year: number, ids: number[]): Promise<boolean> {
    const wrapper = oData.getEntitySetWrapper(EntitySetName.PrEldps);

    try {
        await wrapper.action(OdataActionName.PrEldpsGenerateEldp, null, {
            Year: year,
            EmployeeIds: ids
        });
    } catch (e) {
        logger.error("Error generating ELDPs", e);
        throw e;
    }

    return true;
}

export async function getEmployeesWithAttendance(oData: OData, year: number): Promise<number[]> {
    const query = oData.getEntitySetWrapper(EntitySetName.PrAttendances).query()
            .filter(`${PrAttendanceEntity.Year} eq ${year}`)
            .expand(PrAttendanceEntity.Employment, (q) => {
                q.expand(PrEmploymentEntity.Employee, (q2) => {
                    q2.select(PrEmployeeEntity.Id);
                });
            });

    const employeeIds = new Set<number>();

    try {
        const res = await query.fetchData<IPrAttendanceEntity[]>();
        res.value?.forEach((attendance) => {
            const employeeId = attendance.Employment?.Employee?.Id;
            if (employeeId) {
                employeeIds.add(employeeId);
            }
        });

    } catch (e) {
        return [];
    }

    return Array.from(employeeIds.values());
}

export function getEldpAddYearOptions(defaultYear: number): ISelectItem[] {
    const max = Math.max(defaultYear ?? 0, getUtcDayjs(getWorkDate()).year());
    const min = Math.min(max, 2015);
    const years: ISelectItem[] = [];
    for (let i = max; i >= min; i--) {
        years.push({
            id: i,
            label: i.toString()
        });
    }
    return years;
}
